/**
 * Canonical delivery pipeline values stored on bookings (photoDeliveryStatus / videoDeliveryStatus).
 * Legacy human-readable values are normalized in normalizeDeliveryStatus().
 */
import { getLastEventDate } from './bookingEventStatus';

export const DELIVERY_EVENT_UPCOMING = 'event_upcoming';
export const DELIVERY_NOT_SENT = 'not_sent_to_editor';
export const DELIVERY_SENT_EDITOR = 'sent_to_editor';
export const DELIVERY_EDITING = 'editing_in_progress';
export const DELIVERY_DELIVERED = 'delivered';

export const DELIVERY_STATUS_ORDER = [
  DELIVERY_EVENT_UPCOMING,
  DELIVERY_NOT_SENT,
  DELIVERY_SENT_EDITOR,
  DELIVERY_EDITING,
  DELIVERY_DELIVERED
];

export const DELIVERY_STATUS_LABELS = {
  [DELIVERY_EVENT_UPCOMING]: 'Event is upcoming',
  [DELIVERY_NOT_SENT]: 'Not sent to editor',
  [DELIVERY_SENT_EDITOR]: 'Sent to editor',
  [DELIVERY_EDITING]: 'Editing in progress',
  [DELIVERY_DELIVERED]: 'Delivered'
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Event today or in the future - delivery pipeline not started by default. */
export const isBookingEventUpcoming = (booking) => {
  const last = getLastEventDate(booking);
  if (!last) return true;
  return startOfDay(last) >= startOfDay(new Date());
};

/** @param {string} slug */
export const getDeliveryStatusLabel = (slug) =>
  DELIVERY_STATUS_LABELS[slug] || DELIVERY_STATUS_LABELS[DELIVERY_SENT_EDITOR];

/** Dropdown options for admin - hide "Event is upcoming" after the event day. */
export const getDeliveryStatusOptionsForBooking = (booking) => {
  if (isBookingEventUpcoming(booking)) {
    return DELIVERY_STATUS_ORDER;
  }
  return DELIVERY_STATUS_ORDER.filter((slug) => slug !== DELIVERY_EVENT_UPCOMING);
};

/**
 * @param {string|undefined|null} raw
 * @returns {typeof DELIVERY_NOT_SENT|typeof DELIVERY_SENT_EDITOR|typeof DELIVERY_EDITING|typeof DELIVERY_DELIVERED}
 */
export const normalizeDeliveryStatus = (raw) => {
  if (raw == null || raw === '') return DELIVERY_SENT_EDITOR;
  const s = String(raw).trim();
  const lower = s.toLowerCase().replace(/\s+/g, '_');

  if (
    lower === DELIVERY_EVENT_UPCOMING ||
    lower === 'event_upcoming' ||
    s === 'Event is upcoming'
  ) {
    return DELIVERY_EVENT_UPCOMING;
  }
  if (
    lower === DELIVERY_NOT_SENT ||
    lower === 'not_sent_to_editor' ||
    s === 'Not sent to editor'
  ) {
    return DELIVERY_NOT_SENT;
  }
  if (
    lower === DELIVERY_DELIVERED ||
    lower === 'delivered' ||
    s === 'Delivered'
  ) {
    return DELIVERY_DELIVERED;
  }
  if (
    lower === DELIVERY_EDITING ||
    lower === 'editing_in_progress' ||
    s === 'Editing in progress' ||
    lower === 'in_progress' ||
    s === 'In Progress' ||
    lower === 'processing' ||
    s === 'Processing'
  ) {
    return DELIVERY_EDITING;
  }
  if (
    lower === DELIVERY_SENT_EDITOR ||
    lower === 'sent_to_editor' ||
    s === 'Sent to editor' ||
    lower === 'pending' ||
    s === 'Pending'
  ) {
    return DELIVERY_SENT_EDITOR;
  }
  return DELIVERY_SENT_EDITOR;
};

/**
 * Booking-aware delivery status for admin UI and Firestore saves.
 * Upcoming events default to "Event is upcoming" instead of legacy "Sent to editor".
 */
export const resolveBookingDeliveryStatus = (booking, raw) => {
  const hasRaw = raw != null && String(raw).trim() !== '';
  const normalized = hasRaw ? normalizeDeliveryStatus(raw) : null;

  if (isBookingEventUpcoming(booking)) {
    if (!hasRaw || normalized === DELIVERY_SENT_EDITOR) {
      return DELIVERY_EVENT_UPCOMING;
    }
    return normalized;
  }

  if (normalized === DELIVERY_EVENT_UPCOMING) {
    return DELIVERY_NOT_SENT;
  }

  return normalized ?? DELIVERY_SENT_EDITOR;
};

/** CSS suffix for delivery-status-select / badges (no spaces). */
export const deliveryStatusToCssSuffix = (slug) => {
  const n = normalizeDeliveryStatus(slug);
  if (n === DELIVERY_EVENT_UPCOMING) return 'event-upcoming';
  switch (n) {
    case DELIVERY_NOT_SENT:
      return 'not-sent';
    case DELIVERY_SENT_EDITOR:
      return 'sent-editor';
    case DELIVERY_EDITING:
      return 'editing';
    case DELIVERY_DELIVERED:
      return 'delivered';
    default:
      return 'sent-editor';
  }
};

/** @param {string|undefined|null} slug */
export const isDeliveryComplete = (slug) => normalizeDeliveryStatus(slug) === DELIVERY_DELIVERED;
